import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import {
  getDoctorsForPool,
  changeDoctorActivity,
  removeDoctor,
  restoreDoctor,
  updatePriority,
  getAllVerticalsForPoolManager,
} from '../../services/api/pool-manager';
import { RegularText, BoldText } from '../common/Text';
import LoadingComponent from '../common/LoadingComponent';
import ConfirmModal from './ConfirmModal';
import PoolRosterView from './PoolRosterView';
import AddDoctorModal from './AddDoctorModal';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(75, 46, 131, 0.06);
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1eff7;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const GhostButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.75rem;
  padding: 6px 12px;
  border: 1px solid #d9d2ef;
  border-radius: 8px;
  color: #4b2e83;
  background: #f7f4fd;
  cursor: pointer;
  transition: background 0.15s ease;
  :hover {
    background: #efe9fb;
  }
`;

const FilterSelect = styled.select`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.75rem;
  padding: 6px 10px;
  border: 1px solid #d9d2ef;
  border-radius: 8px;
  color: #4b2e83;
  background: #ffffff;
  cursor: pointer;
  outline: none;
  :focus {
    border-color: #4b2e83;
  }
`;

const DoctorsGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.wide ? 'repeat(auto-fill, minmax(20rem, 1fr))' : '1fr'};
  gap: 12px;
  margin-top: 16px;
  max-height: ${(props) => (props.wide ? 'none' : '30rem')};
  overflow-y: ${(props) => (props.wide ? 'visible' : 'auto')};
`;

const DoctorCard = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: ${(props) =>
    props.removed
      ? '#f4f3f7'
      : props.selected
      ? 'linear-gradient(135deg, #f3eefc, #ede7fa)'
      : '#fbfafe'};
  border: 1px ${(props) => (props.removed ? 'dashed' : 'solid')}
    ${(props) =>
      props.removed ? '#cfc9dd' : props.selected ? '#c9b8ee' : '#eeecf6'};
  border-radius: 12px;
  opacity: ${(props) => (props.removed ? 0.75 : 1)};
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
  :hover {
    ${(props) =>
      props.clickable
        ? 'transform: translateY(-1px); box-shadow: 0 6px 16px rgba(75,46,131,0.08);'
        : ''}
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${(props) => (props.withCheckbox ? '12px' : '0')};
  min-width: 0;
`;

const SubLine = styled(RegularText)`
  color: #7a7391;
  font-size: 0.8rem;
  margin-top: 2px;
`;

const PriceTag = styled.span`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.8rem;
  color: #1f6f4a;
  background: #e7f6ee;
  border-radius: 8px;
  padding: 3px 9px;
  margin-top: 4px;
  align-self: flex-start;
`;

const Right = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 12px;
`;

const Checkbox = styled.input`
  width: 17px;
  height: 17px;
  cursor: pointer;
  accent-color: #4b2e83;
`;

const ToggleButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  padding: 5px 12px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  color: #ffffff;
  min-width: 5.5rem;
  background: ${(props) => (props.active ? '#2fa060' : '#b0aabf')};
  transition: opacity 0.15s ease;
  :hover {
    opacity: 0.88;
  }
  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.72rem;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1px solid #e2b6b3;
  cursor: pointer;
  color: #a1170f;
  background: #fdf2f1;
  transition: background 0.15s ease;
  :hover {
    background: #fbe3e1;
  }
`;

const RestoreButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.72rem;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1px solid #b9d3c3;
  cursor: pointer;
  color: #1f6f4a;
  background: #eaf6ef;
  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PriorityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
`;

const PriorityLabel = styled(RegularText)`
  color: #7a7391;
  font-size: 0.78rem;
`;

const PriorityInput = styled.input`
  width: 4rem;
  padding: 4px 8px;
  border: 1px solid #ddd6ee;
  border-radius: 8px;
  font-size: 0.8rem;
  font-family: BrandonText-Medium, Lato, sans-serif;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const SavePriorityBtn = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.72rem;
  padding: 4px 10px;
  border: 1px solid #d9d2ef;
  border-radius: 8px;
  color: #4b2e83;
  background: #f7f4fd;
  cursor: pointer;
  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HelperText = styled(RegularText)`
  color: #8a84a0;
  margin-top: 12px;
`;

const isActive = (doctor) => doctor.active === 1 || doctor.active === true;
const isRemoved = (doctor) => doctor.removed === 1 || doctor.removed === true;

const formatPrice = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isNaN(num) ? `${value}` : `₹${num}`;
};

class PoolDoctorsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      doctors: [],
      loading: false,
      error: null,
      togglingId: null,
      restoringId: null,
      confirmRemove: null,
      removing: false,
      rosterDoctor: null,
      showAddModal: false,
      priorityEdits: {},
      savingPriorityId: null,
      verticals: [],
      verticalFilter: '',
      statusFilter: '',
    };
  }

  componentDidMount() {
    this.getDoctors();
    this.getVerticals();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.poolId !== this.props.poolId) {
      this.getDoctors();
    }
  }

  getDoctors = () => {
    const { poolId, onDoctorsLoaded } = this.props;
    if (!poolId) {
      return;
    }
    this.setState({ loading: true, error: null });
    getDoctorsForPool(poolId)
      .then((res) => {
        if (res && res.message === 'success') {
          const doctors = res.specialists || [];
          this.setState({ doctors });
          if (onDoctorsLoaded) {
            onDoctorsLoaded(doctors);
          }
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to fetch doctors');
        }
      })
      .catch((err) => {
        const message = err.message || 'Failed to fetch doctors';
        toast.error(message);
        this.setState({ doctors: [], error: message });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  // Verticals power the client-side filter dropdown. Best-effort: a failure
  // just leaves the filter empty (shows all doctors), never blocks the list.
  getVerticals = () => {
    getAllVerticalsForPoolManager()
      .then((res) => {
        if (res && res.message === 'success') {
          this.setState({ verticals: res.verticals || [] });
        }
      })
      .catch(() => {});
  };

  onCardClick = (doctor) => {
    if (this.props.rosterOnClick) {
      this.setState({ rosterDoctor: doctor });
    }
  };

  onToggleActive = (doctor, e) => {
    if (e) {
      e.stopPropagation();
    }
    const { poolId } = this.props;
    const nextActive = !isActive(doctor);
    this.setState({ togglingId: doctor.doctorId });
    changeDoctorActivity(poolId, doctor.doctorId, nextActive)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success(
            `${doctor.first_name || 'Doctor'} marked ${
              nextActive ? 'active' : 'inactive'
            }.`
          );
          this.setState((prev) => ({
            doctors: prev.doctors.map((d) =>
              `${d.doctorId}` === `${doctor.doctorId}`
                ? { ...d, active: nextActive ? 1 : 0 }
                : d
            ),
          }));
        } else {
          throw new Error(
            (res && res.errorMessage) || 'Failed to update doctor status'
          );
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to update doctor status');
      })
      .finally(() => {
        this.setState({ togglingId: null });
      });
  };

  onRestore = (doctor, e) => {
    if (e) {
      e.stopPropagation();
    }
    const { poolId } = this.props;
    this.setState({ restoringId: doctor.doctorId });
    restoreDoctor(poolId, doctor.doctorId)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success(`${doctor.first_name || 'Doctor'} restored to the pool.`);
          this.getDoctors();
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to restore doctor');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to restore doctor');
      })
      .finally(() => {
        this.setState({ restoringId: null });
      });
  };

  getPriorityValue = (doctor) => {
    const { priorityEdits } = this.state;
    const key = `${doctor.doctorId}`;
    if (Object.prototype.hasOwnProperty.call(priorityEdits, key)) {
      return priorityEdits[key];
    }
    return doctor.priority !== undefined && doctor.priority !== null
      ? doctor.priority
      : 0;
  };

  onPriorityChange = (doctorId, value) => {
    this.setState((prev) => ({
      priorityEdits: { ...prev.priorityEdits, [`${doctorId}`]: value },
    }));
  };

  onSavePriority = (doctor) => {
    const { poolId } = this.props;
    const value = parseInt(this.getPriorityValue(doctor), 10);
    if (Number.isNaN(value)) {
      toast.error('Enter a valid priority.');
      return;
    }
    this.setState({ savingPriorityId: doctor.doctorId });
    updatePriority(poolId, doctor.doctorId, value, [], null)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success('Priority updated.');
          this.setState((prev) => {
            const edits = { ...prev.priorityEdits };
            delete edits[`${doctor.doctorId}`];
            return {
              priorityEdits: edits,
              doctors: prev.doctors.map((d) =>
                `${d.doctorId}` === `${doctor.doctorId}`
                  ? { ...d, priority: value }
                  : d
              ),
            };
          });
        } else {
          throw new Error(
            (res && res.errorMessage) || 'Failed to update priority'
          );
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to update priority');
      })
      .finally(() => {
        this.setState({ savingPriorityId: null });
      });
  };

  onConfirmRemove = () => {
    const { poolId } = this.props;
    const { confirmRemove } = this.state;
    if (!confirmRemove) {
      return;
    }
    this.setState({ removing: true });
    removeDoctor(poolId, confirmRemove.doctorId)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success(
            `${confirmRemove.first_name || 'Doctor'} removed from the pool.`
          );
          this.setState({ confirmRemove: null });
          this.getDoctors();
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to remove doctor');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to remove doctor');
      })
      .finally(() => {
        this.setState({ removing: false });
      });
  };

  isChecked = (doctorId) => {
    const { selectedDoctorIds } = this.props;
    return (selectedDoctorIds || []).some((id) => `${id}` === `${doctorId}`);
  };

  // Single source of truth for the vertical + status filters, shared by
  // render and onToggleAll so "Select / Clear all" can never act on rows
  // hidden by a filter.
  applyFilters = (list) => {
    const { verticalFilter, statusFilter } = this.state;
    return list.filter((d) => {
      if (verticalFilter && `${d.verticalId}` !== `${verticalFilter}`) {
        return false;
      }
      if (statusFilter === 'active') {
        return isActive(d) && !isRemoved(d);
      }
      if (statusFilter === 'inactive') {
        return !isActive(d) && !isRemoved(d);
      }
      if (statusFilter === 'removed') {
        return isRemoved(d);
      }
      return true;
    });
  };

  onToggleAll = () => {
    const { doctors } = this.state;
    const { selectedDoctorIds, onSelectionChange } = this.props;
    if (!onSelectionChange) {
      return;
    }
    // Operate only on the currently visible (filtered) doctors so
    // "Select / Clear all" never silently selects rows hidden by a filter.
    const visible = this.applyFilters(doctors);
    const allIds = visible.map((d) => d.doctorId);
    const allSelected =
      allIds.length > 0 && allIds.every((id) => this.isChecked(id));
    onSelectionChange(allSelected ? [] : allIds, selectedDoctorIds);
  };

  onToggle = (doctorId) => {
    const { selectedDoctorIds, onSelectionChange } = this.props;
    if (!onSelectionChange) {
      return;
    }
    const current = selectedDoctorIds || [];
    const next = this.isChecked(doctorId)
      ? current.filter((id) => `${id}` !== `${doctorId}`)
      : [...current, doctorId];
    onSelectionChange(next, current);
  };

  render() {
    const {
      doctors,
      loading,
      error,
      togglingId,
      restoringId,
      confirmRemove,
      removing,
      rosterDoctor,
      showAddModal,
      savingPriorityId,
      verticals,
      verticalFilter,
      statusFilter,
    } = this.state;
    const {
      poolId,
      poolName,
      selectable,
      removable,
      addable,
      rosterOnClick,
      wide,
    } = this.props;
    const clickable = !!rosterOnClick;
    // Client-side vertical + status filters: the pool's full doctor set is
    // already loaded, so narrowing is just a filter on the array.
    const shownDoctors = this.applyFilters(doctors);
    const isFiltered = !!verticalFilter || !!statusFilter;

    return (
      <Card>
        <HeaderRow>
          <BoldText fontSize="1.05rem">
            {`Doctors in ${poolName || 'Pool'} · ${shownDoctors.length}${
              isFiltered ? ` of ${doctors.length}` : ''
            }`}
          </BoldText>
          <HeaderActions>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => this.setState({ statusFilter: e.target.value })}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="removed">Removed</option>
            </FilterSelect>
            <FilterSelect
              value={verticalFilter}
              onChange={(e) =>
                this.setState({ verticalFilter: e.target.value })
              }
            >
              <option value="">All verticals</option>
              {verticals.map((v) => (
                <option key={v.verticalId} value={v.verticalId}>
                  {v.verticalName}
                </option>
              ))}
            </FilterSelect>
            {selectable && shownDoctors.length ? (
              <GhostButton onClick={this.onToggleAll}>
                Select / Clear all
              </GhostButton>
            ) : null}
            {addable ? (
              <GhostButton onClick={() => this.setState({ showAddModal: true })}>
                + Add doctor
              </GhostButton>
            ) : null}
          </HeaderActions>
        </HeaderRow>
        {loading ? (
          <LoadingComponent />
        ) : error ? (
          <HelperText>{error}</HelperText>
        ) : (
          <DoctorsGrid wide={wide}>
            {shownDoctors.map((doctor) => {
              const price = formatPrice(doctor.visitPrice);
              const active = isActive(doctor);
              const removed = isRemoved(doctor);
              return (
                <DoctorCard
                  key={doctor.doctorId}
                  clickable={clickable && !removed}
                  removed={removed}
                  onClick={
                    clickable && !removed
                      ? () => this.onCardClick(doctor)
                      : undefined
                  }
                >
                  <Left>
                    {selectable ? (
                      <Checkbox
                        type="checkbox"
                        checked={this.isChecked(doctor.doctorId)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => this.onToggle(doctor.doctorId)}
                      />
                    ) : null}
                    <Meta withCheckbox={selectable}>
                      <BoldText>
                        {doctor.first_name || `Doctor ${doctor.doctorId}`}
                      </BoldText>
                      <SubLine>
                        {`ID: ${doctor.doctorId}`}
                        {doctor.vertical ? ` · ${doctor.vertical}` : ''}
                      </SubLine>
                      {price ? (
                        <PriceTag>{`Visit price: ${price}`}</PriceTag>
                      ) : (
                        <PriceTag
                          style={{ color: '#8a84a0', background: '#f2f0f7' }}
                        >
                          Visit price: —
                        </PriceTag>
                      )}
                      {!removed ? (
                        <PriorityRow onClick={(e) => e.stopPropagation()}>
                          <PriorityLabel>Priority</PriorityLabel>
                          <PriorityInput
                            type="number"
                            value={this.getPriorityValue(doctor)}
                            onChange={(e) =>
                              this.onPriorityChange(
                                doctor.doctorId,
                                e.target.value
                              )
                            }
                          />
                          <SavePriorityBtn
                            disabled={
                              `${savingPriorityId}` === `${doctor.doctorId}`
                            }
                            onClick={() => this.onSavePriority(doctor)}
                          >
                            {`${savingPriorityId}` === `${doctor.doctorId}`
                              ? '…'
                              : 'Save'}
                          </SavePriorityBtn>
                        </PriorityRow>
                      ) : null}
                    </Meta>
                  </Left>
                  <Right>
                    {removed ? (
                      <React.Fragment>
                        <ToggleButton active={false} disabled>
                          Inactive
                        </ToggleButton>
                        <RestoreButton
                          disabled={`${restoringId}` === `${doctor.doctorId}`}
                          onClick={(e) => this.onRestore(doctor, e)}
                        >
                          {`${restoringId}` === `${doctor.doctorId}`
                            ? '…'
                            : 'Restore'}
                        </RestoreButton>
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <ToggleButton
                          active={active}
                          disabled={`${togglingId}` === `${doctor.doctorId}`}
                          onClick={(e) => this.onToggleActive(doctor, e)}
                        >
                          {`${togglingId}` === `${doctor.doctorId}`
                            ? '…'
                            : active
                            ? 'Active'
                            : 'Inactive'}
                        </ToggleButton>
                        {removable ? (
                          <RemoveButton
                            onClick={(e) => {
                              e.stopPropagation();
                              this.setState({ confirmRemove: doctor });
                            }}
                          >
                            Remove
                          </RemoveButton>
                        ) : null}
                      </React.Fragment>
                    )}
                  </Right>
                </DoctorCard>
              );
            })}
            {!doctors.length ? (
              <HelperText>No doctors in this pool.</HelperText>
            ) : null}
          </DoctorsGrid>
        )}

        <ConfirmModal
          isOpen={!!confirmRemove}
          title="Remove doctor from pool"
          danger
          loading={removing}
          confirmText="Remove"
          message={
            confirmRemove
              ? `Remove ${
                  confirmRemove.first_name || `Doctor ${confirmRemove.doctorId}`
                } from ${poolName || 'this pool'}?`
              : ''
          }
          onConfirm={this.onConfirmRemove}
          onCancel={() => this.setState({ confirmRemove: null })}
        />

        <Modal
          isOpen={!!rosterDoctor}
          toggle={() => this.setState({ rosterDoctor: null })}
          size="lg"
          centered
        >
          <ModalHeader toggle={() => this.setState({ rosterDoctor: null })}>
            {rosterDoctor
              ? `Roster · ${rosterDoctor.first_name || `Doctor ${rosterDoctor.doctorId}`}`
              : 'Roster'}
          </ModalHeader>
          <ModalBody>
            {rosterDoctor ? (
              <PoolRosterView
                doctorId={rosterDoctor.doctorId}
                poolId={poolId}
                poolName={poolName}
              />
            ) : null}
          </ModalBody>
        </Modal>

        <AddDoctorModal
          isOpen={showAddModal}
          poolId={poolId}
          poolName={poolName}
          onClose={() => this.setState({ showAddModal: false })}
          onAdded={this.getDoctors}
        />
      </Card>
    );
  }
}

export default PoolDoctorsList;
